import { Classes } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { interpolatePRGn } from 'd3-scale-chromatic'
import getContrastText, { lighten } from '../../utils/getContrastRatio'

function scoreToStyle(score) {
  if (score === 0) {
    return { backgroundColor: 'white', color: 'rgba(0, 0, 0, 0.75)' }
  }
  const backgroundColor = lighten(interpolatePRGn((score + 4) / 8), 0.25)
  const color = getContrastText(backgroundColor)
  return { backgroundColor, color, fontWeight: Math.abs(score) >= 3 ? 'bold' : 'normal' }
}

function buildTooltip(cell, zThresholds) {
  if (cell.filtered) {
    return `Diff: ${cell.diff_pp}pp < ${cell.min_diff_pp}pp min\n\u2192 Score: 0`
  }
  const thresholds = [0, ...zThresholds]
  const lines = [`Diff: ${cell.diff_pp}pp (min: ${cell.min_diff_pp}pp)`]
  if (cell.z !== null) {
    lines.push(`z: ${cell.z}, p: ${cell.p}`)
    const absZ = Math.abs(cell.z)
    const absScore = Math.abs(cell.score)
    if (absScore === 0) {
      lines.push(`${absZ.toFixed(2)} < ${zThresholds[0]} \u2192 Score: 0`)
    } else if (absScore >= thresholds.length - 1) {
      lines.push(`${absZ.toFixed(2)} > ${thresholds[thresholds.length - 1]} \u2192 Score: ${cell.score}`)
    } else {
      lines.push(`${thresholds[absScore]} < ${absZ.toFixed(2)} < ${thresholds[absScore + 1]} \u2192 Score: ${cell.score}`)
    }
  }
  return lines.join('\n')
}

const MAJOR_DISAGREE = '2px solid red'       // one scores 0, other doesn't
const MINOR_DISAGREE = '2px solid #FFA500'   // both nonzero but different magnitude

function DataTable({ title, data, showTooltips, useZtest }) {
  const { segment_names, rows, z_thresholds: zThresholds = [1.28, 1.96, 2.58, 3.29] } = data
  let currentCategory = null

  return (
    <div style={{ flex: 1, overflow: 'auto' }}>
      <table
        className={[Classes.HTML_TABLE, Classes.HTML_TABLE_CONDENSED, Classes.INTERACTIVE].join(' ')}
        style={{ width: '100%', fontSize: '12px' }}
      >
        <thead>
          <tr>
            <th>{title}</th>
            {segment_names.map((name, i) => (
              <th key={`mean-${i}`} style={{ textAlign: 'center', fontSize: '11px' }}>
                {name}
              </th>
            ))}
            <th style={{ textAlign: 'center' }}>Total</th>
            {segment_names.map((name, i) => (
              <th key={`idx-${i}`} style={{ textAlign: 'center', fontSize: '11px' }}>
                {name}
              </th>
            ))}
          </tr>
          <tr>
            <th></th>
            <th colSpan={segment_names.length + 1} style={{ textAlign: 'center', fontSize: '11px', color: '#888' }}>
              means
            </th>
            <th colSpan={segment_names.length} style={{ textAlign: 'center', fontSize: '11px', color: '#888' }}>
              index scores
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => {
            const categoryRow =
              row.category !== currentCategory
                ? (() => {
                    currentCategory = row.category
                    return (
                      <tr key={`cat-${r}`}>
                        <td
                          colSpan={segment_names.length * 2 + 2}
                          style={{ background: '#e2efda', fontWeight: 'bold', fontStyle: 'italic' }}
                        >
                          {row.category}
                        </td>
                      </tr>
                    )
                  })()
                : null

            const cells = useZtest ? row.ztest : row.production

            return [
              categoryRow,
              <tr key={r}>
                <td style={{ background: row.is_edge ? '#ffff00' : 'white', whiteSpace: 'nowrap' }}>
                  {row.name}
                </td>
                {row.seg_means.map((m, i) => (
                  <td key={`m-${i}`} style={{ textAlign: 'right' }}>
                    {m != null && !isNaN(m) ? m.toFixed(2) : '—'}
                  </td>
                ))}
                <td style={{ textAlign: 'right' }}>{row.total_mean != null && !isNaN(row.total_mean) ? row.total_mean.toFixed(4) : '—'}</td>
                {cells.map((cell, i) => {
                  const ztScore = row.ztest[i].score
                  const prodScore = row.production[i].score
                  const oneIsZero = (ztScore === 0) !== (prodScore === 0)
                  const bothNonzeroDifferent = ztScore !== 0 && prodScore !== 0 && ztScore !== prodScore
                  let borderStyle = {}
                  if (oneIsZero) {
                    borderStyle = { outline: MAJOR_DISAGREE }
                  } else if (bothNonzeroDifferent) {
                    borderStyle = { outline: MINOR_DISAGREE }
                  }
                  const style = {
                    ...scoreToStyle(cell.score),
                    textAlign: 'center',
                    ...borderStyle,
                  }
                  if (showTooltips) {
                    return (
                      <td key={`i-${i}`} style={style}>
                        <Tooltip2 content={<pre style={{ margin: 0 }}>{buildTooltip(cell, zThresholds)}</pre>}>
                          <span>{cell.index}</span>
                        </Tooltip2>
                      </td>
                    )
                  }
                  return (
                    <td key={`i-${i}`} style={style}>
                      <Tooltip2 content={`Score: ${cell.score}`}>
                        <span>{cell.index}</span>
                      </Tooltip2>
                    </td>
                  )
                })}
              </tr>,
            ]
          })}
        </tbody>
      </table>
    </div>
  )
}

export default function SandboxTable({ data }) {
  if (!data) return null

  return (
    <div style={{ display: 'flex', gap: '20px', padding: '12px 20px', overflow: 'auto' }}>
      <DataTable title="Production (lookup tables)" data={data} showTooltips={false} useZtest={false} />
      <DataTable title="New (z-test)" data={data} showTooltips={true} useZtest={true} />
    </div>
  )
}
